import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle, UserPlus, ClockCounterClockwise } from "@phosphor-icons/react";

const inputCls = "mt-1 w-full h-11 px-3 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500";

function DetailRow({ label, value, bold }) {
  if (!value) return null;
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className={`text-right text-slate-900 ${bold ? "font-bold" : ""}`}>{value}</span>
    </div>
  );
}

function ReferralForm({ token, onAdded }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      await api.post(`/recruit/${token}/referral`, f);
      setF({ name: "", email: "", phone: "", note: "" });
      onAdded();
    } catch (e2) {
      setErr(e2.response?.data?.detail?.toString() || e2.message);
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} data-testid="referral-form">
      <div className="mb-3">
        <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Their name *</label>
        <input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
          data-testid="referral-name-input" className={inputCls} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Their email</label>
          <input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })}
            data-testid="referral-email-input" className={inputCls} />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Their phone</label>
          <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })}
            data-testid="referral-phone-input" className={inputCls} />
        </div>
      </div>
      <div className="mb-3">
        <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Anything we should know? (optional)</label>
        <textarea rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })}
          data-testid="referral-note-input"
          className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500" />
      </div>
      <p className="text-xs text-slate-500 mb-3">Please include at least an email or a phone number.</p>
      {err && <div className="mb-3 text-sm text-rose-600" data-testid="referral-error">{err}</div>}
      <button type="submit" disabled={busy} data-testid="referral-submit-btn"
        className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-md disabled:opacity-60">
        {busy ? "Sending…" : "Send recommendation"}
      </button>
    </form>
  );
}

export default function RecruitRespond() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [info, setInfo] = useState(null);
  const [state, setState] = useState("loading"); // loading | notfound | ready
  const [panel, setPanel] = useState(null); // interested | later | refer
  const [doneType, setDoneType] = useState(null);
  const [refCount, setRefCount] = useState(0);
  const [form, setForm] = useState({ available_date: "", comment: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/recruit/${token}`).then((r) => {
      setInfo(r.data);
      setRefCount(r.data.referrals_sent || 0);
      if (r.data.responded) setDoneType(r.data.responded.type);
      setState("ready");
      const a = params.get("action");
      if (r.data.status === "open" && !r.data.responded) {
        if (a === "refer") setPanel("refer");
        else if (a === "later") setPanel("later");
      } else if (r.data.status === "open" && a === "refer") setPanel("refer");
    }).catch(() => setState("notfound"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const respond = async (type) => {
    setBusy(true);
    try {
      await api.post(`/recruit/${token}/respond`, { type, ...form });
      setDoneType(type);
      setPanel("refer-prompt");
    } catch (e) {
      alert(e.response?.data?.detail?.toString() || e.message);
    } finally { setBusy(false); }
  };

  if (state === "loading") return <div className="min-h-screen bg-slate-100 flex items-center justify-center text-slate-500">Loading…</div>;

  if (state === "notfound") {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white border border-slate-200 rounded-lg p-8 max-w-md text-center" data-testid="recruit-invalid">
          <h1 className="text-xl font-bold text-slate-900">This link isn't valid</h1>
          <p className="text-slate-500 mt-2 text-sm">It may have been removed. Please contact the company directly if you're interested in work.</p>
        </div>
      </div>
    );
  }

  const closed = info.status === "closed";

  return (
    <div className="min-h-screen bg-slate-100 py-6 px-4">
      <div className="max-w-xl mx-auto">
        <div className="bg-white border border-slate-200 rounded-lg p-6 sm:p-8" data-testid="recruit-page">
          <div className="text-[11px] font-bold text-emerald-600 tracking-[2px] uppercase">{info.company}</div>
          <h1 className="text-2xl sm:text-3xl font-black text-slate-900 mt-1">New project opportunity</h1>
          <div className="w-16 h-1 bg-emerald-600 rounded mt-2 mb-4"></div>

          {closed ? (
            <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-md text-slate-600" data-testid="recruit-closed">
              <p className="font-semibold text-slate-800">This opportunity has been filled or closed.</p>
              <p className="text-sm mt-1">Thanks for your interest — we'll reach out when the next project comes up.</p>
            </div>
          ) : (
            <>
              <p className="text-slate-600">Hi {info.first_name}, we have an upcoming project that may be a good fit for you:</p>

              <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-md" data-testid="recruit-details">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Project details</div>
                <DetailRow label="Position" value={info.title} bold />
                <DetailRow label="Location" value={info.location} />
                <DetailRow label="Pay rate" value={info.pay_rate ? `$${Number(info.pay_rate).toFixed(2)} / hr` : ""} bold />
                <DetailRow label="Per diem" value={info.per_diem ? `$${Number(info.per_diem).toFixed(2)} / day` : ""} bold />
                <DetailRow label="Expected duration" value={info.duration} />
                <DetailRow label="Anticipated start" value={info.start_date} />
              </div>
              {info.notes && <p className="mt-3 text-sm text-slate-600">{info.notes}</p>}
              {info.respond_by && (
                <p className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md text-amber-800 text-sm" data-testid="recruit-deadline">
                  If you want to be considered for this position, please respond by <b>{info.respond_by}</b>.
                </p>
              )}

              {doneType && panel !== "refer" && (
                <div className="mt-5 p-4 bg-emerald-50 border border-emerald-200 rounded-md flex items-start gap-3" data-testid="recruit-done-banner">
                  <CheckCircle size={22} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold text-emerald-800">
                      {doneType === "interested" ? "Got it — you're interested!" : "Got it — we'll keep you in mind."}
                    </p>
                    <p className="text-sm text-emerald-700 mt-0.5">We have your response and will be in touch.</p>
                  </div>
                </div>
              )}

              {(panel === "refer-prompt" || (doneType && panel !== "refer")) && (
                <div className="mt-4 p-4 border border-slate-200 rounded-md">
                  <p className="font-semibold text-slate-800 text-sm">Know someone else who might be a fit?</p>
                  <button onClick={() => setPanel("refer")} data-testid="open-referral-after-btn"
                    className="mt-2 h-11 px-5 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-md inline-flex items-center gap-2">
                    <UserPlus size={16} weight="bold" /> Recommend someone
                  </button>
                  {refCount > 0 && <p className="text-xs text-emerald-700 mt-2" data-testid="referral-count">You've recommended {refCount} {refCount === 1 ? "person" : "people"} — thank you!</p>}
                </div>
              )}

              {!doneType && !panel && (
                <div className="mt-6 flex flex-col gap-3">
                  <button onClick={() => setPanel("interested")} data-testid="interested-btn"
                    className="h-14 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-lg rounded-md">
                    I'm interested
                  </button>
                  <button onClick={() => setPanel("refer")} data-testid="recommend-btn"
                    className="h-14 bg-slate-900 hover:bg-slate-800 text-white font-bold text-lg rounded-md">
                    Recommend someone
                  </button>
                  <button onClick={() => setPanel("later")} data-testid="keep-in-mind-btn"
                    className="text-sm text-slate-500 underline underline-offset-4 hover:text-slate-800 py-1">
                    Not available right now — but keep me in mind for future work
                  </button>
                </div>
              )}

              {panel === "interested" && !doneType && (
                <div className="mt-5 p-4 border border-emerald-200 bg-emerald-50/50 rounded-md" data-testid="interested-panel">
                  <p className="font-bold text-slate-900">Great! Two quick optional questions:</p>
                  <div className="mt-3">
                    <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Earliest date you could start</label>
                    <input type="date" value={form.available_date}
                      onChange={(e) => setForm({ ...form, available_date: e.target.value })}
                      data-testid="interested-date-input" className={inputCls} />
                  </div>
                  <div className="mt-3">
                    <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Anything we should know?</label>
                    <textarea rows={2} value={form.comment}
                      onChange={(e) => setForm({ ...form, comment: e.target.value })}
                      data-testid="interested-comment-input"
                      className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500" />
                  </div>
                  <div className="mt-4 flex gap-2">
                    <button onClick={() => respond("interested")} disabled={busy} data-testid="interested-submit-btn"
                      className="flex-1 h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-md disabled:opacity-60">
                      {busy ? "Sending…" : "Confirm — I'm interested"}
                    </button>
                    <button onClick={() => setPanel(null)} className="h-12 px-4 border border-slate-300 rounded-md text-slate-600 font-semibold">Back</button>
                  </div>
                </div>
              )}

              {panel === "later" && !doneType && (
                <div className="mt-5 p-4 border border-slate-200 rounded-md" data-testid="keep-in-mind-panel">
                  <p className="font-bold text-slate-900 flex items-center gap-2"><ClockCounterClockwise size={18} weight="bold" /> We'll keep you in mind</p>
                  <div className="mt-3">
                    <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Available for work after (optional)</label>
                    <input type="date" value={form.available_date}
                      onChange={(e) => setForm({ ...form, available_date: e.target.value })}
                      data-testid="keep-in-mind-date-input" className={inputCls} />
                  </div>
                  <div className="mt-4 flex gap-2">
                    <button onClick={() => respond("keep_in_mind")} disabled={busy} data-testid="keep-in-mind-submit-btn"
                      className="flex-1 h-12 bg-slate-700 hover:bg-slate-800 text-white font-bold rounded-md disabled:opacity-60">
                      {busy ? "Sending…" : "Keep me in mind"}
                    </button>
                    <button onClick={() => setPanel(null)} className="h-12 px-4 border border-slate-300 rounded-md text-slate-600 font-semibold">Back</button>
                  </div>
                </div>
              )}

              {panel === "refer" && (
                <div className="mt-5 p-4 border border-slate-200 rounded-md" data-testid="refer-panel">
                  <p className="font-bold text-slate-900">Recommend someone</p>
                  <p className="text-sm text-slate-500 mt-0.5 mb-3">We'll reach out to them directly. You can recommend more than one person.</p>
                  {refCount > 0 && <p className="text-xs text-emerald-700 mb-2" data-testid="referral-count">Recommended so far: {refCount}</p>}
                  <ReferralForm token={token} onAdded={() => setRefCount((c) => c + 1)} />
                  <button onClick={() => setPanel(doneType ? "refer-prompt" : null)} data-testid="refer-back-btn"
                    className="mt-3 w-full h-10 border border-slate-300 rounded-md text-slate-600 font-semibold text-sm">
                    {doneType ? "Done" : "Back"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
        <p className="text-center text-xs text-slate-400 mt-4">This link is private to you — please don't share it.</p>
      </div>
    </div>
  );
}
